import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { mapOptionProvider } from '../../../../constants/provider';
import PricingCard from './components/pricing-card';
import { Label } from '@/components/ui/label';
import { useEffect, useState } from 'react';
import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { fNameRegion } from '@/utils/formatLabel';
import { Separator } from '@/components/ui/separator';
import { ServerlessGPUIcon } from '@/components/ui/icons';
import { useGetServerlessInstancePricing } from '@/service/serverless-gpu/hook';
import { Skeleton } from '@/components/ui/skeleton';
import { toastSomethingError } from '@/components/ui/toast-content';

const ServerlessGPUPricing = () => {
  const [provider, setProvider] = useState('aws');
  const [region, setRegion] = useState('us-east-1');

  const { data, isLoading, error } = useGetServerlessInstancePricing();
  useEffect(() => {
    if (error) {
      toastSomethingError(error);
    }
  }, [error]);

  const result = data?.map((item) => ({
    ...item,
    price: {
      on_demand: {
        hours: item.price_per_hour,
        minutes: item.price_per_hour / 60,
        seconds: item.price_per_hour / 3600,
      },
      spot: {
        hours: item.spot_price_per_hour,
        minutes: item.spot_price_per_hour / 60,
        seconds: item.spot_price_per_hour / 3600,
      },
      storage: {
        month: item.storage_price_per_month || 0,
      },
    },
  }));

  const optionInstanceType = result
    ?.filter((item) => item.instance_provider.toLowerCase() === provider)
    ?.filter((item) => item.instance_region === region);

  const optionsProvider = Array.from(
    new Set(result?.map((item) => item.instance_provider.toLowerCase()))
  ).map((providerKey) => {
    const mappedProvider =
      mapOptionProvider[providerKey as keyof typeof mapOptionProvider];
    return {
      provider: providerKey,
      ...(mappedProvider || {
        label: providerKey,
        provider: providerKey,
        icon: ServerlessGPUIcon,
        iconDark: ServerlessGPUIcon,
      }),
    };
  });

  const optionsRegion = Array.from(
    new Set(
      data
        ?.filter((item) => item.instance_provider.toLowerCase() === provider)
        ?.map((item) => item.instance_region)
    )
  ).map((region) => ({
    label: fNameRegion(region),
    value: region,
  }));

  useEffect(() => {
    if (data?.length && optionsProvider?.length) {
      setProvider(optionsProvider[0].provider);
    }
  }, [data, optionsProvider]);

  useEffect(() => {
    setRegion(optionsRegion?.[0]?.value);
  }, [optionsRegion, provider]);

  return (
    <div className="container mt-8">
      <div className="mt-4 flex items-center justify-between">
        <div className="text-base font-semibold">Pricing</div>
      </div>
      {isLoading ? (
        <div className="mt-4 flex gap-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <>
          <RadioGroup
            value={provider}
            onValueChange={(e) => {
              setProvider(e);
            }}
            className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3"
          >
            {optionsProvider?.map((option) => (
              <div key={option.provider}>
                <RadioGroupItem
                  value={option.provider}
                  id={option.provider}
                  className="peer sr-only"
                />
                <Label
                  htmlFor={option.provider}
                  className="flex h-11 cursor-pointer items-center gap-2 rounded-md border-2 border-muted bg-popover px-4 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-muted-foreground [&:has([data-state=checked])]:border-muted-foreground"
                >
                  {option.icon && (
                    <option.icon className="inline h-6 w-6 dark:hidden" />
                  )}
                  {option.iconDark && (
                    <option.iconDark className="hidden h-6 w-6 dark:inline" />
                  )}
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
          <Select
            value={region}
            onValueChange={(e) => {
              setRegion(e);
            }}
          >
            <SelectTrigger className="mt-4 max-w-60">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {optionsRegion?.map((option) => (
                <SelectItem key={option.label} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="mt-4 flex items-center gap-2 overflow-hidden">
            <div className={'text-nowrap font-semibold text-muted-foreground'}>
              Instance Type
            </div>
            <Separator />
          </div>
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            {optionInstanceType?.map((item) => (
              <PricingCard
                key={item.instance_type}
                instance_type={item.instance_type}
                price={item.price}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ServerlessGPUPricing;
